import React, { useState, useEffect } from 'react';
import { settingsApi } from '../../api/settings';
import { modelsApi } from '../../api/models';
import { useTheme } from '../../context/ThemeContext';
import WhatsNew from './WhatsNew';
import CreativeStylePanel from './CreativeStylePanel';
import InviteWriter from './InviteWriter';
import './SettingsDialog.css';
import ConfirmModal from '../Common/ConfirmModal';
import { useConfirm } from '../Common/useConfirm';
import { TextSizeStepper } from '../Common/TextSizeStepper';
import { CHAT_TEXT_SIZE, readChatZoom } from '../../utils/textSizeRange';
import ModelPicker from '../Common/ModelPicker';
import { OWN_KEY_SWITCH_GUIDE } from '../../utils/betaAllowance';

// Fixed pair, not part of the checked catalog menu — matches backend's
// PERPLEXITY_LEARN_MODELS (config/model_catalog.py). The Learn Panel row is
// the only Model Preferences row that offers these, in addition to whatever
// is in the writer's checked menu.
const PERPLEXITY_LEARN_MODELS = [
  { id: 'perplexity/sonar-pro', display_name: 'Perplexity Sonar Pro' },
  { id: 'perplexity/sonar', display_name: 'Perplexity Sonar' },
];

const MODEL_PREFERENCE_TASKS = [
  { key: 'chat', label: 'Chat Conversations' },
  { key: 'learn', label: 'Learn Panel (Web Search)' },
  { key: 'summary', label: 'Session Summaries' },
  // Labels say "Book Notes" / "Series Notes" (the product names the writer
  // sees) even though the internal keys stay bible_generation /
  // series_bible_generation — label-only rename, key untouched.
  { key: 'bible_generation', label: 'Book Notes Generation' },
  { key: 'series_bible_generation', label: 'Series Notes Generation' },
  { key: 'context_summary', label: 'Context Summaries' },
  { key: 'card_generation', label: 'Note Generation' },
  { key: 'story_board_generation', label: 'Story Board Generation' },
  { key: 'prose_scene_generation', label: 'Prose Generation' },
];

const DISPLAY_MODE_OPTIONS = [
  { key: 'dark', label: 'Dark', icon: '🌙', description: 'Dark backgrounds, light text' },
  { key: 'light', label: 'Light', icon: '☀️', description: 'Warm cream tones, dark text' },
  { key: 'paperwhite', label: 'High Contrast', icon: '📄', description: 'Black & white for e-ink displays' },
];

const WORKING_METHOD_OPTIONS = [
  {
    key: 'supportive',
    label: 'Supportive',
    description: 'Encouraging feedback that focuses on possibilities. Celebrates what\'s working before suggesting changes.'
  },
  {
    key: 'balanced',
    label: 'Balanced',
    description: 'Balanced encouragement with honest feedback. Acknowledges strengths while clearly identifying areas to improve.'
  },
  {
    key: 'direct',
    label: 'Direct',
    description: 'Straightforward and efficient feedback. No hedging or unnecessary softening. Pushes you to do your best work.'
  }
];

const SettingsDialog = ({ isOpen, onClose, focusSection = null }) => {
  const { mode, setMode } = useTheme();
  const [zoomLevel, setZoomLevel] = useState(() => (
    readChatZoom(localStorage.getItem(CHAT_TEXT_SIZE.storageKey))
  ));
  const [settings, setSettings] = useState(null);
  const [modelMenu, setModelMenu] = useState({ default: [], added: [] });
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [workingMethod, setWorkingMethod] = useState('balanced');
  // Bumped whenever Restore Defaults changes the menu server-side, so the
  // (independently-fetching) ModelPicker below remounts and re-reads it
  // rather than going stale — ModelPicker owns its own menu state and has
  // no other way to learn this dialog just mutated it out from under it.
  const [modelMenuVersion, setModelMenuVersion] = useState(0);
  const { confirm, modalProps } = useConfirm();

  // Flattened menu (default + added) for the Model Preferences per-task
  // pickers — id/display_name only. ModelPicker (My Models, below) owns its
  // own menu fetching independently.
  const flatModels = [...modelMenu.default, ...modelMenu.added];

  // Apply zoom level to document. readChatZoom already snapped the
  // stored percent onto the step list, and this writes that step back.
  useEffect(() => {
    if (!Number.isFinite(zoomLevel)) return;
    document.documentElement.style.setProperty('--zoom-level', String(zoomLevel / 100));
    localStorage.setItem(CHAT_TEXT_SIZE.storageKey, String(zoomLevel));
    window.dispatchEvent(new Event('chat-zoom-change'));
  }, [zoomLevel]);

  // Chat may have changed the size in this tab while the dialog was closed
  // ('storage' only fires in other tabs), so re-read it on every open.
  useEffect(() => {
    if (!isOpen) return;
    setZoomLevel(readChatZoom(localStorage.getItem(CHAT_TEXT_SIZE.storageKey)));
  }, [isOpen]);

  // Apply saved zoom on mount
  useEffect(() => {
    const saved = localStorage.getItem(CHAT_TEXT_SIZE.storageKey);
    if (saved) {
      const zoom = readChatZoom(saved);
      if (Number.isFinite(zoom)) {
        document.documentElement.style.setProperty('--zoom-level', String(zoom / 100));
      }
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadSettings();
      loadModelMenu();
      loadPreferences();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || focusSection !== 'api-key' || loading) return;
    const section = document.getElementById('openrouter-api-key');
    if (!section) return;
    section.scrollIntoView({ block: 'start' });
    section.querySelector('input')?.focus();
  }, [isOpen, focusSection, loading, settings]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsApi.getSettings();
      // Unwrap data from { data: {...} } response format
      setSettings(response?.data || response);
      setError(null);
    } catch (err) {
      setError('Failed to load settings');
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPreferences = async () => {
    try {
      const response = await settingsApi.getPreferences();
      const prefs = response?.data || response;
      if (prefs?.working_method) {
        setWorkingMethod(prefs.working_method);
      }
    } catch (err) {
      console.error('Error loading preferences:', err);
      // Don't show error - just use default
    }
  };

  const loadModelMenu = async () => {
    try {
      const response = await modelsApi.getAvailable();
      setModelMenu(response?.models || { default: [], added: [] });
    } catch (err) {
      console.error('Error loading model menu:', err);
    }
  };

  const handleSaveApiKey = async () => {
    if (!apiKey.trim()) {
      setError('Please enter an API key');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await settingsApi.setApiKey(apiKey);
      window.dispatchEvent(new Event('openrouter:balance-changed'));
      // Reload full settings after saving (setApiKey returns partial response)
      const response = await settingsApi.getSettings();
      setSettings(response?.data || response);
      setApiKey('');
      setSuccessMessage('API key saved and verified successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save API key');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteApiKey = async () => {
    const ok = await confirm({ title: 'Remove API key?', message: 'Are you sure you want to remove your API key? This will disable AI features.', confirmLabel: 'Remove', destructive: true });
    if (!ok) return;

    try {
      setSaving(true);
      setError(null);
      await settingsApi.deleteApiKey();
      window.dispatchEvent(new Event('openrouter:balance-changed'));
      const response = await settingsApi.getSettings();
      setSettings(response?.data || response);
      setSuccessMessage('API key removed');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete API key');
    } finally {
      setSaving(false);
    }
  };

  const handleModelChange = async (functionName, modelId) => {
    try {
      setError(null);
      await settingsApi.updateModels({ [functionName]: modelId });
      // Reload full settings to ensure consistency
      const response = await settingsApi.getSettings();
      setSettings(response?.data || response);
      setSuccessMessage('Model preference updated');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err) {
      setError(err.message || 'Failed to update model');
    }
  };

  const handleResetModels = async () => {
    const ok2 = await confirm({ title: 'Reset preferences?', message: 'Reset all model preferences to defaults?', confirmLabel: 'Reset', destructive: true });
    if (!ok2) return;

    try {
      setError(null);
      await settingsApi.resetModels();
      // Reload full settings to ensure consistency
      const response = await settingsApi.getSettings();
      setSettings(response?.data || response);
      setSuccessMessage('Models reset to defaults');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err) {
      setError(err.message || 'Failed to reset models');
    }
  };

  const handleRestoreMyModels = async () => {
    const ok = await confirm({
      title: 'Restore default models?',
      message: 'Re-check every default model in your menu. Models you added yourself are left untouched.',
      confirmLabel: 'Restore',
      destructive: true,
    });
    if (!ok) return;

    try {
      setError(null);
      const response = await settingsApi.restoreDefaultModels();
      const models = response?.data?.models || response?.models;
      if (models) setModelMenu(models);
      setModelMenuVersion((v) => v + 1);
      setSuccessMessage('Default models restored');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err) {
      setError(err.message || 'Failed to restore default models');
    }
  };

  const handleWorkingMethodChange = async (method) => {
    const previousMethod = workingMethod;
    setWorkingMethod(method); // Optimistic update

    try {
      setError(null);
      await settingsApi.updatePreferences({ working_method: method });
      setSuccessMessage('Communication style updated');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err) {
      setWorkingMethod(previousMethod); // Revert on error
      setError(err.message || 'Failed to update preference');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="settings-dialog-backdrop" onClick={onClose}>
      <div className="settings-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="settings-dialog-header">
          <h2>Settings</h2>
          <button className="settings-dialog-close" onClick={onClose}>×</button>
        </div>

        <div className="settings-dialog-content">
              {error && (
                <div className="settings-error">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="settings-success">
                  {successMessage}
                </div>
              )}

              {/* Appearance is local. It does not wait on the settings read. */}
              <section className="settings-section">
                <h3>Appearance</h3>
                <div className="display-mode-row">
                  <span className="display-mode-label">Display Mode</span>
                  <div className="display-mode-options">
                    {DISPLAY_MODE_OPTIONS.map(option => (
                      <button
                        key={option.key}
                        className={`display-mode-btn ${mode === option.key ? 'active' : ''}`}
                        onClick={() => setMode(option.key)}
                        title={option.description}
                      >
                        <span className="display-mode-icon">{option.icon}</span>
                        <span className="display-mode-name">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="zoom-control-row">
                  <span className="zoom-label">Text Size</span>
                  <div className="zoom-slider-container">
                    <TextSizeStepper
                      value={zoomLevel}
                      onChange={setZoomLevel}
                      min={CHAT_TEXT_SIZE.min}
                      max={CHAT_TEXT_SIZE.max}
                      step={CHAT_TEXT_SIZE.step}
                    />
                    <span className="zoom-percentage">{zoomLevel}%</span>
                  </div>
                </div>
              </section>

              {loading ? (
                <p className="settings-loading">Loading the rest of settings…</p>
              ) : (
                <>
              {/* Working Method Section */}
              <section className="settings-section">
                <h3>Communication Style</h3>
                <p className="settings-section-description">
                  How would you like Brainstormy to communicate with you?
                </p>
                <div className="working-method-options">
                  {WORKING_METHOD_OPTIONS.map(option => (
                    <label
                      key={option.key}
                      className={`working-method-option ${workingMethod === option.key ? 'working-method-option--selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="workingMethod"
                        value={option.key}
                        checked={workingMethod === option.key}
                        onChange={() => handleWorkingMethodChange(option.key)}
                      />
                      <div className="working-method-content">
                        <span className="working-method-label">{option.label}</span>
                        <span className="working-method-description">{option.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </section>

              <InviteWriter />

              {/* API Key Section. The server decides who may paste a key. */}
              {(settings?.can_manage_own_key || settings?.can_switch_to_own_key) && (
              <section className="settings-section" id="openrouter-api-key">
                <h3>OpenRouter API Key</h3>
                <p className="settings-section-description">
                  Your API key is used to access AI models. You only pay for what you use.
                </p>
                {(settings?.can_switch_to_own_key || settings?.key_provisioning === 'own_needed') && (
                  <p className="settings-section-description">
                    {OWN_KEY_SWITCH_GUIDE}
                  </p>
                )}
                <p className="settings-section-description">
                  Keep your own copy of this key. Once saved, we can't show it to you again.
                </p>

                <div className="api-key-status">
                  {settings?.has_api_key && settings?.can_manage_own_key !== false ? (
                    <>
                      <div className="status-indicator status-valid">
                        <span className="status-icon">✓</span>
                        <span>API Key Configured</span>
                      </div>
                      {settings.credits_remaining !== null && settings.credits_remaining !== undefined && (
                        <div className="credit-balance">
                          Balance: ${settings.credits_remaining?.toFixed(2) || '0.00'}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="status-indicator status-not-set">
                      <span className="status-icon">⚠</span>
                      <span>No API Key Configured</span>
                    </div>
                  )}
                </div>

                <div className="api-key-input-group">
                  <div className="input-with-toggle">
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="sk-or-v1-..."
                      className="api-key-input"
                      disabled={saving}
                    />
                    <button
                      className="toggle-visibility-btn"
                      onClick={() => setShowApiKey(!showApiKey)}
                      type="button"
                    >
                      {showApiKey ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  <button
                    className="save-api-key-btn"
                    onClick={handleSaveApiKey}
                    disabled={saving || !apiKey.trim()}
                  >
                    {saving ? 'Saving...' : 'Save & Verify'}
                  </button>
                </div>

                {settings?.has_api_key && settings?.can_manage_own_key !== false && (
                  <button
                    className="delete-api-key-btn"
                    onClick={handleDeleteApiKey}
                    disabled={saving}
                  >
                    Remove API Key
                  </button>
                )}

                <a
                  href="https://openrouter.ai/keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="openrouter-link"
                >
                  Get an API key from OpenRouter →
                </a>
              </section>
              )}

              {/* My Models Section (spec v2 §3) — the writer-curated OpenRouter
                  menu that powers every picker below and on the four
                  generation surfaces (chat, card, Story Board, prose). */}
              <section className="settings-section">
                <div className="section-header-with-action">
                  <h3>My Models</h3>
                  <button
                    className="reset-models-btn"
                    onClick={handleRestoreMyModels}
                    disabled={!settings?.has_api_key}
                  >
                    Restore Defaults
                  </button>
                </div>
                <p className="settings-section-description">
                  Check the models you want available everywhere — chat, note generation, Story Board, and prose. Unchecking anything, including a default, removes it from every picker.
                </p>

                <ModelPicker mode="curate" key={modelMenuVersion} />
              </section>

              {/* Model Preferences Section */}
              <section className="settings-section">
                <div className="section-header-with-action">
                  <h3>Model Preferences</h3>
                  <button
                    className="reset-models-btn"
                    onClick={handleResetModels}
                    disabled={!settings?.has_api_key}
                  >
                    Reset to Defaults
                  </button>
                </div>
                <p className="settings-section-description">
                  Choose which AI models to use for different tasks.
                </p>

                <div className="model-preferences-list">
                  {MODEL_PREFERENCE_TASKS.map(({ key, label }) => {
                    const options = key === 'learn' ? [...flatModels, ...PERPLEXITY_LEARN_MODELS] : flatModels;
                    const current = settings?.model_preferences?.[key];
                    return (
                      <div key={key} className="model-preference-item">
                        <label>{label}</label>
                        <select
                          value={current || ''}
                          onChange={(e) => handleModelChange(key, e.target.value)}
                          disabled={!settings?.has_api_key || options.length === 0}
                          className="model-select"
                        >
                          {current && !options.some((m) => m.id === current) && (
                            <option value={current}>{current}</option>
                          )}
                          {options
                            .filter((m) => m.id !== current)
                            .map((model) => (
                              <option key={model.id} value={model.id}>
                                {model.display_name}
                              </option>
                            ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Creative Style Section (P3.3) */}
              <section className="settings-section">
                <CreativeStylePanel />
              </section>

              {/* What's New Section */}
              <section className="settings-section">
                <h3>What's New</h3>
                <p className="settings-section-description">
                  Recent updates and improvements to Brainstormy.
                </p>
                <WhatsNew />
              </section>

              {/* Done Button */}
              <div className="settings-dialog-footer">
                <button className="settings-done-btn" onClick={onClose}>
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <ConfirmModal {...modalProps} />
    </div>
  );
};

export default SettingsDialog;
